import React, { useState, useRef, useEffect } from 'react';
import { Send, Copy, Download, X, Image as ImageIcon, Loader } from 'lucide-react';

interface Message {
  role: 'user' | 'ai';
  text?: string;
  imageUrl?: string;
  imageFile?: File; // アップロードした画像のFile参照を保持
}

interface ToastProps {
  message: string;
  type: 'success' | 'error';
  onClose: () => void;
}

// トースト通知コンポーネント
const Toast: React.FC<ToastProps> = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(() => {
      onClose();
    }, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`fixed bottom-4 right-4 px-4 py-2 rounded-md shadow-lg ${
      type === 'success' ? 'bg-green-500' : 'bg-red-500'
    } text-white flex items-center`}>
      {message}
      <button onClick={onClose} className="ml-2">
        <X size={16} />
      </button>
    </div>
  );
};

const ChatPage: React.FC = () => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [image, setImage] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const chatRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviewUrl, setImagePreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    chatRef.current?.scrollTo({
      top: chatRef.current.scrollHeight,
      behavior: 'smooth'
    });
  }, [messages]);

  // 画像ファイルが選択されたときにプレビューを表示
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setImage(file);
    
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    } else {
      setImagePreviewUrl(null);
    }
  };

  // 画像プレビューをクリア
  const clearImagePreview = () => {
    setImage(null);
    setImagePreviewUrl(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
  };

  const handleSend = async () => {
    if (!input.trim() && !image) return;

    const formData = new FormData();
    formData.append('prompt', input);
    
    let userMessage: Message = { role: 'user', text: input };
    
    if (image) {
      formData.append('image', image);
      userMessage = { ...userMessage, imageFile: image };
    }
    
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setImage(null);
    setImagePreviewUrl(null);
    setLoading(true);

    try {
      const response = await fetch('https://mastra-worker.kgu-ishizaka.workers.dev', {
        method: 'POST',
        body: formData,
      });

      console.log('✅ Fetch status:', response.status);
      const data = await response.json();
      console.log('✅ Response JSON:', data);

      // エラーレスポンスの処理
      if (data.error) {
        setMessages(prev => [...prev, { role: 'ai', text: `⚠️ エラー: ${data.error}` }]);
        showToast(data.error, 'error');
        return;
      }

      const aiMessage: Message = data.imageUrl
        ? { role: 'ai', imageUrl: data.imageUrl }
        : { role: 'ai', text: data.text || '⚠️ 応答が空でした' };

      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      console.error('❌ Fetch Error:', err);
      setMessages(prev => [...prev, { 
        role: 'ai', 
        text: '❌ 通信エラーが発生しました。サーバーに接続できないか、タイムアウトした可能性があります。' 
      }]);
      showToast('通信エラーが発生しました', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    showToast('テキストをコピーしました', 'success');
  };

  const handleDownload = (content: string, index: number, isImage = false) => {
    const element = document.createElement('a');
    
    if (isImage) {
      // 画像URLからダウンロード
      element.href = content;
      element.download = `image-${index + 1}.png`;
    } else {
      // テキストからダウンロード
      element.href = URL.createObjectURL(new Blob([content], { type: 'text/plain' }));
      element.download = `response-${index + 1}.txt`;
    }
    
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
    
    showToast(isImage ? '画像を保存しました' : 'テキストを保存しました', 'success');
  };

  // ユーザーがアップロードした画像をUIに表示
  const renderUserImage = (file: File) => {
    // ファイルURIはimageFileから生成
    const fileUrl = URL.createObjectURL(file);
    
    return (
      <div className="relative mt-2 inline-block">
        <img 
          src={fileUrl} 
          alt="アップロード画像" 
          className="rounded-md max-h-40 max-w-full object-contain" 
          onLoad={() => URL.revokeObjectURL(fileUrl)} // メモリリーク防止
        />
      </div>
    );
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      <h2 className="text-xl font-bold mb-4">AIコンテンツ作成チャット</h2>

      <div ref={chatRef} className="bg-gray-100 p-4 rounded-lg h-[480px] overflow-y-auto space-y-4">
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`relative p-3 rounded-lg whitespace-pre-wrap break-words shadow-sm ${
              msg.role === 'user' ? 'bg-blue-100 ml-auto max-w-[80%]' : 'bg-white mr-auto max-w-[80%]'
            }`}
          >
            <div className="flex flex-col">
              {msg.text && <p className="text-sm mb-2">{msg.text}</p>}
              
              {/* ユーザーがアップロードした画像を表示 */}
              {msg.role === 'user' && msg.imageFile && renderUserImage(msg.imageFile)}
              
              {/* AIが返した画像を表示 */}
              {msg.role === 'ai' && msg.imageUrl && (
                <img 
                  src={msg.imageUrl} 
                  alt="AI生成画像" 
                  className="rounded-md mb-2 max-w-full" 
                />
              )}
            </div>
            
            {msg.role === 'ai' && (
              <div className="absolute top-2 right-2 flex space-x-2">
                {msg.text && (
                  <button 
                    onClick={() => handleCopy(msg.text!)} 
                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                    title="コピー"
                  >
                    <Copy size={16} />
                  </button>
                )}
                {msg.text && (
                  <button
                    onClick={() => handleDownload(msg.text!, i, false)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                    title="テキストをダウンロード"
                  >
                    <Download size={16} />
                  </button>
                )}
                {msg.imageUrl && (
                  <button
                    onClick={() => handleDownload(msg.imageUrl!, i, true)}
                    className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100"
                    title="画像をダウンロード"
                  >
                    <Download size={16} />
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
        {loading && (
          <div className="flex items-center justify-center text-gray-500">
            <Loader size={16} className="animate-spin mr-2" />
            <span className="text-sm">AIが処理しています...</span>
          </div>
        )}
      </div>

      <div className="mt-4 space-y-2">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="何を作りたいですか？"
          className="w-full border px-3 py-2 rounded-md focus:outline-none resize-none"
          rows={3}
        />
        
        <div className="flex items-center space-x-2">
          <label className="flex-1">
            <div className="relative">
              <input
                type="file"
                ref={fileInputRef}
                accept="image/*"
                onChange={handleImageChange}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
              />
              <div className="border border-gray-300 rounded-md px-3 py-2 flex items-center text-gray-600 cursor-pointer hover:bg-gray-50">
                <ImageIcon size={16} className="mr-2" />
                {image ? '画像を変更...' : '画像をアップロード...'}
              </div>
            </div>
          </label>
          
          <button
            onClick={handleSend}
            disabled={loading || (!input.trim() && !image)}
            className={`px-4 py-2 rounded-md flex items-center ${
              loading || (!input.trim() && !image)
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-blue-500 text-white hover:bg-blue-600'
            }`}
          >
            <Send size={16} className="mr-2" />
            送信
          </button>
        </div>
        
        {/* 画像のプレビュー */}
        {imagePreviewUrl && (
          <div className="relative mt-2 inline-block">
            <img 
              src={imagePreviewUrl} 
              alt="画像プレビュー" 
              className="rounded-md max-h-32 max-w-full object-contain" 
            />
            <button
              onClick={clearImagePreview}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 shadow-md hover:bg-red-600"
              title="画像をクリア"
            >
              <X size={12} />
            </button>
          </div>
        )}
      </div>
      
      {/* トースト通知 */}
      {toast && (
        <Toast 
          message={toast.message} 
          type={toast.type} 
          onClose={() => setToast(null)} 
        />
      )}
    </div>
  );
};

export default ChatPage;
